import { Spin } from "antd";
import { memo } from "react";
import { user } from "services/user";
import {
  ChatPaperclipIcon,
  ChatSparkleIcon,
  ChatSuggestionIcon,
} from "components/shared/svg-action-icons";
import { CHAT_SUGGESTION_KEYS } from "../constants";
import MessageContent from "./MessageContent";

const ChatEmptyState = ({
  t,
  variant = "compact",
  hasPromptPermission,
  onSuggestionClick,
}) => {
  const isCompact = variant === "compact";

  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        isCompact
          ? "py-6 px-4 space-y-4"
          : "py-12 px-6 space-y-5 max-w-2xl mx-auto h-full"
      }`}
    >
      <div
        className={`rounded-full bg-primary/10 flex items-center justify-center animate-pulse ${
          isCompact ? "w-16 h-16" : "w-20 h-20"
        }`}
      >
        <ChatSparkleIcon
          className={`text-primary ${isCompact ? "w-8 h-8" : "w-10 h-10"}`}
        />
      </div>

      <div className={isCompact ? "space-y-1.5 max-w-[320px]" : "space-y-2"}>
        <h3
          className={`font-bold text-text-body ${
            isCompact ? "text-[18px]" : "text-2xl"
          }`}
        >
          {t("chat.empty_title")}
        </h3>
        <p
          className={`text-text-muted leading-relaxed font-light ${
            isCompact ? "text-xs" : "text-sm max-w-md"
          }`}
        >
          {t("chat.empty_desc")}
        </p>
      </div>

      <div
        className={`grid grid-cols-2 w-full ${
          isCompact ? "gap-2.5 mt-4 max-w-[360px]" : "gap-4 mt-6"
        }`}
      >
        {CHAT_SUGGESTION_KEYS.map((suggestion) => (
          <button
            key={suggestion.textKey}
            type="button"
            onClick={() => onSuggestionClick(t(suggestion.textKey))}
            disabled={!hasPromptPermission}
            className={`flex flex-col items-start bg-surface border border-border-light hover:border-primary/30 hover:shadow-md transition text-start disabled:opacity-60 disabled:cursor-not-allowed group ${
              isCompact
                ? "p-3 rounded-xl space-y-2"
                : "p-4 rounded-2xl space-y-3 hover:shadow-lg"
            }`}
          >
            <div
              className={`bg-primary/5 rounded-lg group-hover:bg-primary/10 transition ${
                isCompact ? "p-1.5 rounded-lg" : "p-2 rounded-xl"
              }`}
            >
              <ChatSuggestionIcon
                type={suggestion.icon}
                className={isCompact ? "w-5 h-5" : "w-6 h-6"}
              />
            </div>
            <span
              className={`font-semibold text-text-body leading-snug ${
                isCompact ? "text-[11px] truncate-2-lines" : "text-xs"
              }`}
            >
              {t(suggestion.textKey)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const ChatMessageList = ({
  messages,
  loading,
  messageTextClassName,
  variant = "compact",
}) => {
  const isCompact = variant === "compact";
  const avatarSize = isCompact ? "w-[30px] h-[30px]" : "w-[38px] h-[38px]";
  const sparkleSize = isCompact ? "w-3.5 h-3.5" : "w-5 h-5";
  const userAvatarSize = isCompact ? "w-[30px] h-[30px]" : "w-[38px] h-[38px]";

  return (
    <div className={isCompact ? "space-y-4" : "space-y-6 max-w-4xl mx-auto"}>
      {messages.map((msg, idx) => {
        const isUser = msg.role === "user";
        const isError = !isUser && msg.isError;

        return (
          <div
            key={msg.id || idx}
            className={`flex items-start w-full ${
              isCompact ? "gap-2.5" : "gap-4"
            } ${isUser ? "justify-end" : "justify-start"}`}
          >
            {!isUser && (
              <div
                className={`${avatarSize} rounded-full flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5 ${
                  isError ? "bg-danger" : "bg-primary"
                }`}
              >
                <ChatSparkleIcon className={`${sparkleSize} text-white`} />
              </div>
            )}

            <div
              className={`flex flex-col ${isCompact ? "max-w-[76%]" : "max-w-[80%]"}`}
            >
              <div
                className={`shadow-[0_2px_8px_rgba(0,0,0,0.02)] ${
                  isCompact
                    ? "text-xs p-3"
                    : "text-sm p-4 shadow-[0_4px_12px_rgba(0,0,0,0.02)]"
                } ${
                  isUser
                    ? `bg-primary text-white ${isCompact ? "rounded-[18px] rounded-te-none" : "rounded-2xl rounded-te-none"} text-end`
                    : isError
                      ? `bg-danger-bg-soft text-danger ${isCompact ? "rounded-[18px] rounded-ts-none" : "rounded-2xl rounded-ts-none"} text-start border border-danger/40`
                      : `bg-surface text-text-body ${isCompact ? "rounded-[18px] rounded-ts-none" : "rounded-2xl rounded-ts-none"} text-start border border-border-light/50`
                }`}
              >
                <MessageContent
                  className={`${messageTextClassName} block`}
                  content={msg.content}
                />

                {msg.files?.length > 0 && (
                  <div
                    className={`flex items-center flex-wrap ${
                      isCompact ? "gap-2 mt-2" : "gap-3 mt-3"
                    }`}
                  >
                    {msg.files.map((file, fileIdx) => (
                      <div
                        key={file.url || file.filename || fileIdx}
                        className={`flex items-center cursor-pointer ${
                          isCompact
                            ? "gap-1.5 px-2 py-1 rounded-lg text-[10px]"
                            : "gap-2 px-3 py-1.5 rounded-xl text-xs"
                        } ${
                          isUser
                            ? "bg-white/10 text-white hover:bg-white/20"
                            : "bg-primary/5 text-primary hover:bg-primary/10"
                        }`}
                        onClick={() => {
                          if (file.url) {
                            window.open(
                              file.url,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                      >
                        <ChatPaperclipIcon
                          className={isCompact ? "w-3 h-3" : "w-3.5 h-3.5"}
                        />
                        <span
                          className={`truncate ${
                            isCompact
                              ? "font-medium max-w-[120px]"
                              : "font-semibold max-w-[180px]"
                          }`}
                        >
                          {file.filename}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isUser && (
              <img
                src={user?.photo_url || "/icons/person.svg"}
                alt="User"
                className={`${userAvatarSize} object-cover rounded-full flex-shrink-0 mt-0.5 border border-primary/20 ${
                  isCompact ? "" : "shadow-md"
                }`}
                onError={(e) => {
                  e.target.src = "/icons/person.svg";
                }}
              />
            )}
          </div>
        );
      })}

      {loading && (
        <div
          className={`flex items-start w-full justify-start ${
            isCompact ? "gap-2.5" : "gap-4"
          }`}
        >
          <div
            className={`${avatarSize} rounded-full bg-primary flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5`}
          >
            <ChatSparkleIcon className={`${sparkleSize} text-white`} />
          </div>
          <div
            className={`bg-surface border border-border-light/50 ${
              isCompact
                ? "p-3.5 rounded-[18px] rounded-ts-none shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
                : "p-4 rounded-2xl rounded-ts-none shadow-[0_4px_12px_rgba(0,0,0,0.02)]"
            }`}
          >
            <div
              className={`flex items-center ${isCompact ? "gap-1" : "gap-1.5"}`}
            >
              <span
                className={`bg-primary rounded-full dot-bounce ${
                  isCompact ? "w-1.5 h-1.5" : "w-2 h-2"
                }`}
              />
              <span
                className={`bg-primary rounded-full dot-bounce dot-bounce-delay-1 ${
                  isCompact ? "w-1.5 h-1.5" : "w-2 h-2"
                }`}
              />
              <span
                className={`bg-primary rounded-full dot-bounce dot-bounce-delay-2 ${
                  isCompact ? "w-1.5 h-1.5" : "w-2 h-2"
                }`}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ChatMessagesArea = ({
  t,
  variant = "compact",
  messages,
  loading,
  loadingMore,
  fetchLoading,
  isFetching,
  hasPromptPermission,
  messagesContainerRef,
  onScroll,
  onSuggestionClick,
  messageTextClassName,
}) => {
  const isCompact = variant === "compact";

  return (
    <div
      ref={messagesContainerRef}
      onScroll={onScroll}
      className={`flex-1 overflow-y-auto bar ${
        isCompact ? "p-4 space-y-4 pb-24" : "p-6 space-y-6 pb-28"
      }`}
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      {loadingMore && (
        <div className="flex justify-center py-2">
          <Spin size="small" />
        </div>
      )}

      {(fetchLoading || isFetching) && messages.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <Spin size={isCompact ? "default" : "large"} />
        </div>
      ) : messages.length === 0 ? (
        <ChatEmptyState
          t={t}
          variant={variant}
          hasPromptPermission={hasPromptPermission}
          onSuggestionClick={onSuggestionClick}
        />
      ) : (
        <ChatMessageList
          messages={messages}
          loading={loading}
          messageTextClassName={messageTextClassName}
          variant={variant}
        />
      )}
    </div>
  );
};

export default memo(ChatMessagesArea);
